import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import isNumber from 'lodash/isNumber'
import Failure from '@vtex/styleguide/lib/icon/Failure'
import Spinner from '@vtex/styleguide/lib/Spinner'
import { getFriendlyPrice } from '@/checkout-instore-legacy/utils/ProductUtils'
import classnames from 'classnames'
import { getOrderForm } from '@/checkout-instore-legacy/utils/orderFormUtils'
import { getCustomTaxesTotal } from '@/checkout-instore-legacy/utils/getCustomTaxesTotal'

const PRICE_CLASSES_BASED_ON_LENGTH = {
  '<8': 't-heading-2',
  8: 't-heading-3-l t-heading-2-xl ',
  9: 'f2-xl f3-l',
  10: 'f3-xl f4-l mb1-l mb0-xl',
  11: 'f3-xl f4-l mb1-l mb0-xl',
  '>11': 'f4 mb1',
}

function CartSummary({
  itemsTotal,
  shippingTotal,
  giftCards,
  total,
  currencyCode,
  coupon,
  intl,
  removeGiftCard,
  removeCoupon,
  isTakeAwayOnly,
  giftCardPromise,
  discount,
  isValidCart,
  shippingWasFilled,
}) {
  if (!isValidCart) {
    return null
  }

  const shouldHandleExpectedGiftcard =
    giftCardPromise &&
    (giftCardPromise.waitingForArrival || giftCardPromise.shouldPreSelect)
  const expectedGiftCardBalance = shouldHandleExpectedGiftcard
    ? giftCardPromise.balance + giftCardPromise.value
    : 0
  const cartTotal = shippingWasFilled
    ? Math.max(total - expectedGiftCardBalance, 0)
    : Math.max(itemsTotal - (discount || 0), 0)

  const totalPriceLabel =
    cartTotal > 0
      ? formatCurrency(getFriendlyPrice(cartTotal), currencyCode, intl)
      : intl.formatMessage({ id: 'summary.free' })

  const orderForm = getOrderForm()

  const customTaxesTotal = getCustomTaxesTotal(orderForm?.totalizers)

  const hasCustomTaxes = customTaxesTotal !== 0

  const priceCharacterQuantity = totalPriceLabel.replace(/[ ,.\s]/g, '').length
  const totalPriceClass = classnames('fw7', {
    [PRICE_CLASSES_BASED_ON_LENGTH['<8']]: priceCharacterQuantity < 8,
    [PRICE_CLASSES_BASED_ON_LENGTH[priceCharacterQuantity]]:
      priceCharacterQuantity >= 8 && priceCharacterQuantity <= 11,
    [PRICE_CLASSES_BASED_ON_LENGTH['>11']]: priceCharacterQuantity > 11,
  })

  const totalLabelClass = classnames({
    mv1: priceCharacterQuantity < 8,
    'mb1-l mv1-xl': priceCharacterQuantity > 8 && priceCharacterQuantity <= 9,
    mb1: priceCharacterQuantity > 9,
  })

  return (
    <div id="summary-cart-values" className="flex flex-column mh6 mt6 mb3">
      <div className="flex flex-row items-center justify-between fw7 mb5">
        <span className="near-black">
          <FormattedMessage id="summary.cart.summary.itemsTotal" />
        </span>
        <span
          style={{ fontWeight: 'inherit' }}
          id="summary-cart-values-subtotal"
        >
          {formatCurrency(getFriendlyPrice(itemsTotal), currencyCode, intl)}
        </span>
      </div>
      {!isTakeAwayOnly && shippingWasFilled && (
        <div className="flex flex-row items-end justify-between fw7 mb5">
          <span className="near-black">
            <FormattedMessage id="summary.cart.summary.shipping" />
          </span>
          <span
            id="summary-cart-values-shipping"
            style={{ fontWeight: 'inherit' }}
            className={!(shippingTotal > 0) ? 'green' : ''}
          >
            {isNumber(shippingTotal)
              ? shippingTotal > 0
                ? formatCurrency(
                    getFriendlyPrice(shippingTotal),
                    currencyCode,
                    intl
                  )
                : intl.formatMessage({ id: 'summary.free' })
              : intl.formatMessage({
                  id: 'summary.cart.summary.shipping.undefined',
                })}
          </span>
        </div>
      )}
      {hasCustomTaxes && (
        <div className="flex flex-row items-center justify-between fw7 mb5">
          <span className="near-black">
            <FormattedMessage id="summary.cart.summary.customTaxesTotal" />
          </span>
          <span
            style={{ fontWeight: 'inherit' }}
            id="summary-cart-values-subtotal"
          >
            {formatCurrency(customTaxesTotal, currencyCode, intl)}
          </span>
        </div>
      )}
      {coupon && (
        <div className="flex flex-row items-end justify-between mb5 near-black fw7">
          <div className="flex flex-row items-center">
            <FormattedMessage id="summary.cart.summary.coupon" />
            <div
              id="summary-cart-values-coupon-remove"
              className="ml3 c-muted-3 mt2 pointer"
              onClick={removeCoupon}
            >
              <Failure solid />
            </div>
          </div>
          <span>
            <span id="summary-cart-values-coupon" className="c-success">
              {coupon}
            </span>
          </span>
        </div>
      )}
      {shouldHandleExpectedGiftcard ? (
        <div className="flex flex-row items-end justify-between mb5 near-black fw7">
          <FormattedMessage id="summary.cart.summary.giftcard" />
          <span>
            {giftCardPromise.waitingForArrival && (
              <span className="pr3">
                <Spinner size={16} />
              </span>
            )}
            <span
              id={`summary-cart-values-giftcard-promise`}
              className={`${
                giftCardPromise.waitingForArrival ? 'c-muted-2' : 'c-success'
              }`}
            >{`- ${formatCurrency(
              getFriendlyPrice(expectedGiftCardBalance),
              currencyCode,
              intl
            )}`}</span>
          </span>
        </div>
      ) : (
        giftCards.map((giftCard, index) => (
          <div
            key={giftCard.redemptionCode}
            className="flex flex-row items-end justify-between mb5 near-black fw7"
          >
            <div className="flex flex-row items-center">
              <FormattedMessage id="summary.cart.summary.giftcard" />
              <div
                id={`summary-cart-values-giftcard-remove-${index}`}
                className="ml3 c-muted-3 mt2 pointer"
                onClick={() => removeGiftCard(giftCard)}
              >
                <Failure solid />
              </div>
            </div>
            <span
              id={`summary-cart-values-giftcard-${index}`}
              className="c-success"
            >{`- ${formatCurrency(
              getFriendlyPrice(giftCard.balance),
              currencyCode,
              intl
            )}`}</span>
          </div>
        ))
      )}
      {discount && (
        <div className="flex flex-row items-end justify-between mb5">
          <div className="flex flex-row items-center near-black fw7">
            <FormattedMessage id="summary.cart.summary.discount" />
          </div>
          <span
            id="summary-cart-values-discount"
            className="c-success b"
          >{`- ${formatCurrency(
            getFriendlyPrice(discount),
            currencyCode,
            intl
          )}`}</span>
        </div>
      )}
      <div className="flex flex-row items-center justify-between b--light-gray bt-s pt5">
        <span className={`near-black fw7 ${totalLabelClass}`}>
          <FormattedMessage id="summary.cart.summary.total" />
        </span>
        <span id="summary-cart-values-total" className={totalPriceClass}>
          {totalPriceLabel}
        </span>
      </div>
    </div>
  )
}

function formatCurrency(value, currencyCode, intl) {
  return intl.formatNumber(value, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
  })
}

CartSummary.defaultProps = {
  isValidCart: true,
}

CartSummary.propTypes = {
  itemsTotal: PropTypes.number,
  shipping: PropTypes.string,
  shippingTotal: PropTypes.number,
  giftCards: PropTypes.array,
  coupon: PropTypes.string,
  total: PropTypes.number,
  currencyCode: PropTypes.string,
  intl: intlShape,
  removeGiftCard: PropTypes.func.isRequired,
  removeCoupon: PropTypes.func.isRequired,
  isTakeAwayOnly: PropTypes.bool,
  waitingForGiftCardArrival: PropTypes.bool,
  giftCardPromise: PropTypes.object,
  discount: PropTypes.number,
  isValidCart: PropTypes.bool,
  shippingWasFilled: PropTypes.bool,
}

export default injectIntl(CartSummary)
